import { Header } from "portal/components/header/Header";
import { Link, Typography } from "@mui/material";
import { Page } from "portal/components/Page";
import { PortalLanguage, portalLanguageOrDefault } from "../i18nConstants";
import { Trans, useTranslation } from "react-i18next";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import React, { FC, FunctionComponent, ReactNode } from "react";

const _Knowledge: FunctionComponent = () => {
  const { i18n, t } = useTranslation();
  const lang = portalLanguageOrDefault(i18n.language);

  return (
    <>
      <Header title={t("views.knowledge.title")} />
      <Page>
        <Typography variant="h4">
          {t("views.knowledge.softwareRelease")}
        </Typography>

        <ul>
          <li>
            <ManualLink manuals={V_2_3_RELEASE_NOTES}>
              {t("views.knowledge.versionReleaseNotes", {
                version: "v2.3",
                date: new Date(2026, 2, 15).toLocaleString(lang, {
                  month: "long",
                  year: "numeric",
                }),
              })}
            </ManualLink>
          </li>
        </ul>

        <Typography variant="h4">
          {t("views.knowledge.laserWeederManuals")}
        </Typography>

        <ul>
          <li>
            <ManualLink manuals={G1_HARDWARE_MANUALS}>
              {t("views.knowledge.g1HardwareManual")}
            </ManualLink>
          </li>
          <li>
            <ManualLink manuals={G2_HARDWARE_MANUALS}>
              {t("views.knowledge.g2HardwareManual")}
            </ManualLink>
          </li>
          <li>
            <ManualLink manuals={LASERWEEDER_SOFTWARE_MANUALS}>
              {t("views.knowledge.laserWeederSoftwareManual")}
            </ManualLink>
          </li>
          <li>
            <ManualLink manuals={G2_LEGS_MAINTENANCE}>
              {t("views.knowledge.g2LegsMaintenance")}
            </ManualLink>
          </li>
        </ul>

        <Typography variant="h4">
          {t("views.knowledge.videosAndBestPractices")}
        </Typography>

        <ul>
          <li>
            <ManualLink manuals={LASERWEEDER_HOW_TOS}>
              {t("views.knowledge.laserWeederHowToVideos")}
            </ManualLink>
          </li>
          <li>
            <ManualLink manuals={LASERWEEDER_BEST_PRACTICES}>
              {t("views.knowledge.laserWeederBestPractices")}
            </ManualLink>
          </li>
        </ul>

        <Typography variant="h4">{t("views.knowledge.maintenance")}</Typography>

        <ul>
          <li>
            <ManualLink manuals={G1_PREVENTATIVE_MAINTENANCE}>
              {t("views.knowledge.g1PreventativeMaintenance")}
            </ManualLink>
          </li>
          <li>
            <ManualLink manuals={G2_PREVENTATIVE_MAINTENANCE}>
              {t("views.knowledge.g2PreventativeMaintenance")}
            </ManualLink>
          </li>
        </ul>

        <Typography>
          <Trans
            i18nKey="views.knowledge.googleDriveNotice"
            components={{
              link: (
                <Link href="https://www.google.com/url?q=https://drive.google.com/drive/folders/1FpR0nkZMxVCqJjcZm03_CSxxANnHpYOl?usp%3Ddrive_link&sa=D&source=editors&ust=1791838374769757&usg=AOvVaw26XMZKEyhHV61QJisyWT8-">
                  {t("views.knowledge.googleDrive")}
                </Link>
              ),
            }}
          />
        </Typography>
      </Page>
    </>
  );
};
export const Knowledge = withAuthenticationRequired(_Knowledge);

const ManualLink: FC<{
  children: ReactNode;
  manuals: Partial<Record<PortalLanguage, string>>;
}> = ({ children, manuals }) => {
  const { i18n } = useTranslation();
  const lang = portalLanguageOrDefault(i18n.language);

  return (
    <Link href={manuals[lang] ?? manuals["en-US"]} target="_blank">
      {children}
    </Link>
  );
};

const V_2_3_RELEASE_NOTES: Partial<Record<PortalLanguage, string>> = {
  "da-DK":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1fxRpw4UT2eUKwFz23Penkpx5uz70vck_/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791840296111932&usg=AOvVaw2uj3Y-cfEVDJYdyMTuKqnA",
  "de-DE":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1a-gZs00VHqAFO0RbneIiaKXd55EIUVTX/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791840296111751&usg=AOvVaw3pZY2EZPqqhQ6QHWMqWeF_",
  "en-US":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1WUSpA3L-upsO1KNRQtsJS3VtkYWHw7V4/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791839166582280&usg=AOvVaw19WYVo8LfUqDr2DAHY-C4s",
  "es-ES":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1X8twuLok5wlp7A0R_YO4Wc9NeLxP37B_/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791840296111568&usg=AOvVaw1l1I3tVERcGWxwx7uj_ylm",
  "fr-FR":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1dJpXfxHuRkckH7T3Ysu_Nr3RLpmvRI8Z/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791840296111663&usg=AOvVaw0tmOPHmnuDj5L9mB61PIwI",
  "it-IT":
    "https://www.google.com/url?q=https://drive.google.com/file/d/15cBlTkcB5bY-yr5COpBu9xVgIgEPs0Bb/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791840296112296&usg=AOvVaw0pyFO5Iwr0PBTG9l5WqZ45",
  "lt-LT":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1zTag16ob6CujIAKBNw_fnndvm1gCnmjt/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791840296112213&usg=AOvVaw3l-BFi8ehlDCn-Pr-94X5P",
  "nl-NL":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1RN9HHd2LFGeAhQB5-hvHiQgvQrxC9bS4/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791840296111838&usg=AOvVaw1Gejkj6w7DNS5XfcBj4QUh",
  "pl-PL":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1a_Pb31mo1ITxepVmVeDCIVfYIQ3ZhOKt/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791840296112017&usg=AOvVaw1L2g8MobtiDpTVBm_wRBRb",
  "ro-RO":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1EA5oXoHnLEwF82gA-Kie25DIB1XqKgQm/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791840296112126&usg=AOvVaw3GKI54eGt39_niJuqeEFyb",
  "sr-Latn-RS":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1UzCM2GSnCzbdIyuBo3LgdSvDpKHVpFoJ/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791840296112378&usg=AOvVaw1rA7sSbmdgSJBWu9HFKBYa",
  "sv-SE":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1K4deBVAjPRliFRFcxZJi5yfQU26o2fcX/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791840296112568&usg=AOvVaw3FWEJAe_5eVzB5jo7bXk2J",
  "uk-UA":
    "https://www.google.com/url?q=https://drive.google.com/file/d/18V_X51nfXvA0T3HptABkCMqRUm9nc8mv/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791840296112461&usg=AOvVaw2P7cGC7pi8uxTkRGzx0mbj",
};

const G2_HARDWARE_MANUALS: Partial<Record<PortalLanguage, string>> = {
  "da-DK":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1nCximehz6UX53p2JUM2liVvrvyq2s08G/view?usp%3Dsharing&sa=D&source=editors&ust=1791841584164644&usg=AOvVaw3iv0gV1TE_caUkoenNNKjo",
  "de-DE":
    "https://www.google.com/url?q=https://drive.google.com/file/d/16ld5rkX86k9Ey1VI-2B1Q_VaMw3-PlQa/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584164212&usg=AOvVaw0FLCfm80_yELRe1zFpRhy7",
  "en-US":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1fVigkJKvcybbhtTpkME5PmXbRnjqYK0P/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584163584&usg=AOvVaw3Qhd5wuZ39Yfq7m7IJLeDJ",
  "es-ES":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1V_vULhTDSdqS-al7A43WDuazJVsRuJCD/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584163819&usg=AOvVaw3aixnzEcugZHEVbF92gPAy",
  "fr-FR":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1q91Eu-8MGz7BQKWfewAMjb4p-dWgauJi/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584164002&usg=AOvVaw24CdaHwzmwYVOj5N4yRo-X",
  "it-IT":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1tv5mTeRqKhn2WfuOa1jP9mKR8bllx8TK/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584165413&usg=AOvVaw1tIUUKDNz-PvmAEFQl6Ed-",
  "lt-LT":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1Ux7jx2O0WrYlPL2T70OdL9x9ZGeSeDQw/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584165177&usg=AOvVaw0FksPue2DWYjQPADb2lTkd",
  "nl-NL":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1oOorpXT0J2xcFd3NhbRPOpD6u1lT309g/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584164471&usg=AOvVaw1iK1TQZMVDIsiefDlF5G79",
  "pl-PL":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1OIqXFLiag01EVEYhuYHFH-TBI4fFqekx/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584164796&usg=AOvVaw1O9GW5PP0qZsvRaRk-TEXu",
  "ro-RO":
    "https://www.google.com/url?q=https://drive.google.com/file/d/16cNKUVBkhCqIABGhNrD4o5UtE1NVUdjB/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584164952&usg=AOvVaw2RGxchOQrW99sR0Vhg4J5i",
  "sv-SE":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1_4CWo7AklJxqJC7ns_37ZYp7TzqEYFVm/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584165610&usg=AOvVaw25TsCi62jremyS4wyEODJf",
};

const G2_LEGS_MAINTENANCE: Partial<Record<PortalLanguage, string>> = {
  "en-US":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1KHqOzVxK1-_A5_fpFQ0tEryQTYyXEjex/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841262260707&usg=AOvVaw08x3zHlJlDktnumS8GLLHi",
};

const G2_PREVENTATIVE_MAINTENANCE: Partial<Record<PortalLanguage, string>> = {
  "en-US":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1Q1gMl74mCIWiOM6v8BjRDpZbvAOZd8sV/view?usp%3Dsharing&sa=D&source=editors&ust=1791841262264358&usg=AOvVaw2vf1p1MV679UCAeZSyehKk",
};

const LASERWEEDER_SOFTWARE_MANUALS: Partial<Record<PortalLanguage, string>> = {
  "da-DK":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1DMc6RcTwVSl-KYV8AIrdCFLmudjV3ywx/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584166997&usg=AOvVaw0gL5rjgnJfQNPb810a4iU8",
  "de-DE":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1w1ml_RSkck8hyrAhdoRaz4cfOcgD8cVF/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584166674&usg=AOvVaw3uoLdt3PqqlNAfW1LRbHp1",
  "en-US":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1BVLRBfxV7U9bgjvp6lqgbO-jnymTx5pD/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584166060&usg=AOvVaw1o-vt0qmThH1nCR3RLbYk5",
  "es-ES":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1j9RMxSWuxO6CH7QGgMGUkOU-rP_6Q5QB/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584166315&usg=AOvVaw2ZokhkSHiaq7R26Q8TeQMm",
  "fr-FR":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1nQM990lc21GkJd5nHcB2Ij-_-0hHYB1C/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584166520&usg=AOvVaw2gGsxcZEej9nrLLif4c314",
  "it-IT":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1E7w2ec71vfv7d-Knkzk4Tlai2XqxU3ET/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584167617&usg=AOvVaw36eD-W2vdVpfrvadbcz77j",
  "lt-LT":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1d04Q3Wpox_hJWzdXzFFh6zmNJE0Yu62X/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584167495&usg=AOvVaw2ekWiEl7h8FVg8hpmUxo_X",
  "nl-NL":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1Hkx85FAgFOFr-p2y4f0iv0TJgmsEykUI/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584166847&usg=AOvVaw3JFreDFxhiawskrB8sTDyM",
  "pl-PL":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1h2XWWXroCWdcDbtxtoTPG351SAJAFA3T/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584167202&usg=AOvVaw1w3I_RaHYLJ9ejm0WhyPcl",
  "ro-RO":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1trJ922d8HlLCFHWkt2Ye5775zwphHkDu/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584167368&usg=AOvVaw2lmAaU88rrRbXZ5KId15aP",
  "sr-Latn-RS":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1piRkp1YMFKEW0B4lsiFr22v61HickgV1/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584167756&usg=AOvVaw1OMuQdoCZcRtlqgUuTZUxX",
  "sv-SE":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1OUUF7_QxTCd9uFmS9rP0zT5oMuPb7Ff5/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584168069&usg=AOvVaw0VXWYezmuRuDFEvqrwG7Hd",
  "uk-UA":
    "https://www.google.com/url?q=https://drive.google.com/file/d/11F41b4pDNik_j70zre8IquYfBNY6Oa8s/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791841584167906&usg=AOvVaw3WkCqJRBS6CTC31BPvilkO",
};

const LASERWEEDER_BEST_PRACTICES: Partial<Record<PortalLanguage, string>> = {
  "da-DK":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1XhD8o0ZYZ4CIOtffFYwhMyA7XwLh1LjP/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154825798&usg=AOvVaw3GvGGEmY_eZ055tzu-1Ou_",
  "de-DE":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1qI44REH5o_KJhFERiGTOD8bXvcimlxS3/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154825655&usg=AOvVaw2qhwMgj60X_XChwvSSFwnw",
  "en-US":
    "https://www.google.com/url?q=https://drive.google.com/file/d/11dBTG9JqLL2Kd_c5UiVgjCv3sJ_0P8bL/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154825452&usg=AOvVaw283JQ2_mdNdf_NU4XMT2tq",
  "es-ES":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1edVK-UCjzLccEknWTyQZBc00G3YLptzd/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154825517&usg=AOvVaw3v9T1fwLgguq5qU9XrNW3E",
  "fr-FR":
    "https://www.google.com/url?q=https://drive.google.com/file/d/15oxxtlIsIoDtyQmD9ZqysJcpxaFfl3JQ/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154825582&usg=AOvVaw0Xufsm6Tmx7Sh4yKYvrZwN",
  "it-IT":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1ldMr7zpOkNn0A6pRv44TkypH9D95_6rt/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154826088&usg=AOvVaw0na9D3quvPEFkKQykAhg7v",
  "lt-LT":
    "https://www.google.com/url?q=https://drive.google.com/file/d/13oxCwnCWj_vasYWo1nalmEWx-1BdAhLE/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154826020&usg=AOvVaw3LhpTlQEqqU7HnIXzUXRD6",
  "nl-NL":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1YRWuGjSynpu64I-RcdtrbOn_092_wt4z/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154825728&usg=AOvVaw0i8d99aVfojfyoxHVg30Xe",
  "pl-PL":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1ZY4FwReotgjjY82NTIa-8BnhTBOR9WWf/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154825862&usg=AOvVaw2DexuD-C-dP56Yvjhr97KR",
  "ro-RO":
    "https://www.google.com/url?q=https://drive.google.com/file/d/138q9oAexJ4289NDUHZCHZvi-mqNBCGP8/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154825946&usg=AOvVaw3GGp6_mLa_4DK93pM9Nsa7",
  "sr-Latn-RS":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1gNONHZjzYX6cfAfZGdATdWGqXrdXVjpM/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154826154&usg=AOvVaw11EiW99EjThq1fItBZ7lQu",
  "sv-SE":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1r3LOHT0zpfJoeL3pLoX1BhHKnRWWJ3z9/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154826283&usg=AOvVaw0rCQQSZlIboopEIc9bAD5A",
  "uk-UA":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1NOCgEHXrEjaZlzKwgK43LwU4BCApdj75/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154826218&usg=AOvVaw2zp3LYTkIIDVsvolRa5Q0K",
};

const LASERWEEDER_HOW_TOS: Partial<Record<PortalLanguage, string>> = {
  "en-US":
    "https://www.google.com/url?q=https://youtube.com/playlist?list%3DPLI40k5sHWZNvKmZjus0BoLxxsPL2SwZna%26si%3DJRxhQujSm-pAOsfn&sa=D&source=editors&ust=1791838760037114&usg=AOvVaw0kC-RsogTxWGHuGUHbvh1w",
};

const G1_HARDWARE_MANUALS: Partial<Record<PortalLanguage, string>> = {
  "en-US":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1Rs5qbF9Ti9NCxJ41HqMkCk7LkEQEYa_z/view?usp%3Dsharing&sa=D&source=editors&ust=1791835131478983&usg=AOvVaw3pFklfggXCuODuPDdu2v4Q",
};

const G1_PREVENTATIVE_MAINTENANCE: Partial<Record<PortalLanguage, string>> = {
  "da-DK":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1fKlnuj-geOxbfzlZssMN_7ldjiMAm025/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154826827&usg=AOvVaw11KcgkOxgNgfsuu2-XSOph",
  "de-DE":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1MUJBz8qNDw5tvlwVoRnUijEt33by_9xX/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154826683&usg=AOvVaw1TGD-shZfEk7cEFace1CHE",
  "en-US":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1ZKJgixT7Ac76uSAf_oEjh8VnyBQysSx4/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154826469&usg=AOvVaw34dmtEObK2mqLW_wz7whZ5",
  "es-ES":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1M02_4cLW5oTZvn5oDt3R6TXhK3MIRXqU/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154826542&usg=AOvVaw0ZVDOGcVrY1ni4eY4XudSw",
  "fr-FR":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1fnyEX68OqrXlogzsjeOYT1_YzR0Z3Mch/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154826612&usg=AOvVaw1sc5h4RXFw2qTQbmLoF3qm",
  "it-IT":
    "https://www.google.com/url?q=https://drive.google.com/file/d/19SRZAitfJ0EC13oALvaq1mq0nejdflUA/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154827150&usg=AOvVaw2WOkkj6U6RHq8Q_Jz4O2jk",
  "lt-LT":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1U8rPIM9MXdy8vWxTdcipVGl1noiv0dT7/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154827086&usg=AOvVaw2wZbB92cA_601YWuIp54-V",
  "nl-NL":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1kTUio3aX01JPO1n0TEUWHkumGWJ3XMes/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154826762&usg=AOvVaw2mSlYO_lt1wy5MF0spw_Xo",
  "pl-PL":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1ZY4FwReotgjjY82NTIa-8BnhTBOR9WWf/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154826947&usg=AOvVaw3DPUey8HcKE5PReD1klnHE",
  "ro-RO":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1f4IOCt7iOZ90U1eBuqpqE73zAswJ6ozW/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154827020&usg=AOvVaw19cLnT9HoiA2EoU0cgCvqf",
  "sr-Latn-RS":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1ysLY0E4EquPKrV1garPDnxo5TiaMDCwx/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154827214&usg=AOvVaw0Rv7zPdSZObcWTOMKWFS8e",
  "sv-SE":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1UYd91MBd5TWkya1eCcK20v7uyY7Dhzd5/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154827340&usg=AOvVaw3b5Pn-_6s7LHCwxQjvjnal",
  "uk-UA":
    "https://www.google.com/url?q=https://drive.google.com/file/d/1pafPGQPlGjOZfU4b6iYuOdERbdL7YfF7/view?usp%3Ddrive_link&sa=D&source=editors&ust=1791842154827277&usg=AOvVaw0k1TeLTPpvGK8Qf4sjUUB5",
};
